import { useMemo, useCallback } from "react";
import { AgGridReact } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { useCanopyGroupUsersQuery } from "~/graphql/mainHooks";
import { formatFullDateAndTimeWithLocalTimezoneV2 } from "~/utils/datetime";
import { toDbId } from "~/utils/graphql-helpers";

export type AssignedUserRow = {
  userId: number;
  userName: string;
  email: string;
  activeWorkspaces: number;
  lastAccess: string;
};

export const useAssignedUsersTableAgGridOptions = () => {
  const columnDefs: AgGridReact.AgGridReactProps["columnDefs"] = useMemo(() => {
    return [
      {
        field: "userId",
        headerName: "User ID",
        filter: "agNumberColumnFilter",
      },
      {
        field: "userName",
        headerName: "User",
        filter: "agTextColumnFilter",
      },
      {
        field: "email",
        headerName: "Email",
        filter: "agTextColumnFilter",
      },
      {
        field: "activeWorkspaces",
        headerName: "Active workspaces",
        filter: "agNumberColumnFilter",
      },
      {
        field: "lastAccess",
        headerName: "Last access",
        filter: "agDateColumnFilter",
      },
    ];
  }, []);

  return { columnDefs };
};

export const useFetchAssignedUsers = (groupId: string | undefined) => {
  const useCanopyGroupUsersQueryResult = useCanopyGroupUsersQuery({
    variables: {
      input: {
        groupId: groupId ?? "",
      },
    },
    context: withClientContext("staff"),
  });

  const formatDateString = useCallback((dateString: string | undefined | null) => {
    if (!dateString) {
      return "--";
    }
    return formatFullDateAndTimeWithLocalTimezoneV2(
      dateString,
      "MMMM d, yyyy 'at' h:mm a",
      true,
    );
  }, []);

  const rowData = useMemo(() => {
    if (
      useCanopyGroupUsersQueryResult.data?.canopyGroupUsers?.__typename ===
      "CanopyGroupUsersOutput"
    ) {
      return useCanopyGroupUsersQueryResult.data.canopyGroupUsers.users.map(
        (user): AssignedUserRow => ({
          userId: toDbId(user.userId),
          userName: user.fullName || "--",
          email: user.emailAddress || "--",
          activeWorkspaces: user.activeWorkspaceCount || 0,
          lastAccess: formatDateString(user.lastAccessTimestamp ?? null),
        }),
      );
    }
    return [];
  }, [useCanopyGroupUsersQueryResult.data, formatDateString]);

  return {
    rowData,
    useCanopyGroupUsersQueryResult,
  };
};
